// src/frontend/src/components/Common/ExitIntentUpgrade.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';

interface UserPlan {
  planType: string;
  creditsUsed: number;
  creditsTotal: number;
  isTrialActive: boolean;
  trialDaysLeft: number;
}

const ExitIntentUpgrade: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [hasShownBefore, setHasShownBefore] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserPlan();
    }

    // Check if we've shown this before in this session
    const hasShown = sessionStorage.getItem('exitIntentShown');
    if (hasShown === 'true') {
      setHasShownBefore(true);
    }

    // Exit intent detection
    let timeout: NodeJS.Timeout;
    
    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse leaves through the top of the viewport
      if (e.clientY <= 0 && !hasShownBefore && shouldShowExitIntent()) {
        timeout = setTimeout(() => {
          setShowModal(true);
          setHasShownBefore(true);
          sessionStorage.setItem('exitIntentShown', 'true');
        }, 100);
      }
    };

    const handleMouseEnter = () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isAuthenticated, hasShownBefore]);

  const fetchUserPlan = async () => {
    try {
      const response = await api.get('/users/plan');
      setUserPlan(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user plan:', err);
    }
  };

  const shouldShowExitIntent = () => {
    if (!userPlan) return false;
    
    // Show for trial users (especially those close to expiring or who have used sessions)
    if (userPlan.isTrialActive) {
      const creditsPercentageUsed = (userPlan.creditsUsed / userPlan.creditsTotal) * 100;
      return creditsPercentageUsed >= 20 || userPlan.trialDaysLeft <= 7;
    }
    
    // Show for Solo Core users who are using the product
    if (userPlan.planType === 'solo_core') {
      const creditsPercentageUsed = (userPlan.creditsUsed / userPlan.creditsTotal) * 100;
      return creditsPercentageUsed >= 30;
    }
    
    return false;
  };

  const createCheckoutSession = async (planType: string, billingPeriod: string) => {
    setIsCreatingCheckout(true);
    try {
      const response = await api.post('/payments/create-checkout-session', {
        planType,
        billingPeriod
      });
      
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert('Failed to start upgrade. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const getOfferMessage = () => {
    if (!userPlan) return null;
    
    if (userPlan.isTrialActive) {
      if (userPlan.trialDaysLeft <= 3) {
        return {
          title: "Wait! Your trial expires soon!",
          subtitle: `Don't lose access in ${userPlan.trialDaysLeft} day${userPlan.trialDaysLeft === 1 ? '' : 's'}`,
          offer: "Subscribe now and save your progress + get unlimited access",
          urgency: true
        };
      }
      
      const creditsPercentageUsed = (userPlan.creditsUsed / userPlan.creditsTotal) * 100;
      if (creditsPercentageUsed >= 50) {
        return {
          title: "You're loving PM Coach!",
          subtitle: `You've used ${Math.round(creditsPercentageUsed)}% of your trial sessions`,
          offer: "Upgrade now for unlimited practice + advanced features",
          urgency: false
        };
      }
      
      return {
        title: "Before you go...",
        subtitle: "Unlock your full PM potential",
        offer: "Get unlimited coaching sessions + real-time tips",
        urgency: false
      };
    }
    
    if (userPlan.planType === 'solo_core') {
      return {
        title: "Upgrade to Pro for 3x More Practice",
        subtitle: "You're making great progress!",
        offer: "Get 60 sessions/month + real-time coaching tips",
        urgency: false
      };
    }
    
    return {
      title: "Don't miss out!",
      subtitle: "Special offer for active users",
      offer: "Upgrade your PM coaching experience today",
      urgency: false
    };
  };

  if (!showModal || !userPlan) return null;

  const message = getOfferMessage();
  if (!message) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl transform transition-all duration-300 ${
        message.urgency ? 'border-4 border-red-500' : ''
      }`}>
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Urgency indicator */}
        {message.urgency && (
          <div className="flex items-center justify-center mb-4">
            <div className="animate-pulse">
              <svg className="w-12 h-12 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="text-center mb-6">
          <h2 className={`text-2xl font-bold mb-2 ${message.urgency ? 'text-red-600' : 'text-gray-900'}`}>
            {message.title}
          </h2>
          <p className="text-gray-600 mb-2">{message.subtitle}</p>
          <p className="text-lg font-medium text-blue-600">{message.offer}</p>
        </div>

        {/* Special exit intent offer */}
        {userPlan.isTrialActive && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center mb-2">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold text-green-800">Limited Time: No Setup Fee</span>
            </div>
            <p className="text-green-700 text-sm text-center">
              Subscribe now and skip the usual $50 setup fee!
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-3">
          <button
            onClick={() => createCheckoutSession(
              userPlan.planType === 'solo_core' ? 'solo_pro' : 'solo_pro', 
              'monthly'
            )}
            disabled={isCreatingCheckout}
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 ${
              message.urgency 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isCreatingCheckout ? 'Setting Up Your Account...' : 
             userPlan.planType === 'solo_core' ? 'Upgrade to Pro - $39/month' : 'Get Pro - $39/month'}
          </button>
          
          {userPlan.isTrialActive && (
            <button
              onClick={() => createCheckoutSession('solo_core', 'monthly')}
              disabled={isCreatingCheckout}
              className="w-full py-2 px-6 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isCreatingCheckout ? 'Setting Up...' : 'Start with Core - $19/month'}
            </button>
          )}
          
          <button
            onClick={handleClose}
            className="w-full py-2 px-6 text-gray-500 hover:text-gray-700 transition-colors text-sm"
          >
            Maybe later
          </button>
        </div>

        {/* Trust indicators */}
        <div className="mt-6 pt-4 border-t border-gray-200 text-center">
          <div className="flex justify-center items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Cancel anytime
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              Secure payment
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Instant access
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentUpgrade;